import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { formatDate } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { ITaskPatientChart, TaskService } from "../../../../../services/task.service";
import { taskStatusNameColor } from "../../../../../common/vue-helpers/modifiers/task-status-color";
import { AssetsService } from "../../../../../services/assets-service";
import { DownloadService } from "../../../../../services/download.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { PrintService } from "../../../../../services/print.service";
import { IEmployeeList } from "../../../../../resources-vue/vue-interfaces/i-reassign-visit";
import { ITypeaheadOptions } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import { NavigationService } from "../../../../../services/navigation.service";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { ParentPermissionEnum } from "../../../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../../../enums/PermissionActionEnum";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import TablePrompt from "../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import VisitDetailsHomeCare from "../../../../CentralCalendar/PatientCalendar/components/VisitDetailsHomeCare/VisitDetailsHomeCare.vue";
import EmptyTextHandler from "../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";

export default defineComponent({
    components: {
        EmptyTextHandler,
        TablePrompt,
        VisitDetailsHomeCare,
        FormSection,
        TypeaheadInput
    },
    props: {
        task: {
            default: null
        },
        columns: {
            type: Number,
            default: 7
        },
        getPatientTasks: {
            type: Function
        }
    },
    data(vm) {
        return {
            _analyticsService: null as AnalyticsService,
            _permissionManager: null as PermissionManager,
            _taskService: null as TaskService,
            _navigationService: null as NavigationService,
            _printService: null as PrintService,
            _toastrService: null as ToastrService,
            _downloadService: null as DownloadService,
            _assetService: null as AssetsService,
            printingInProgress: false as boolean,
            downloadInProgress: false as boolean,
            isDeleteRequested: false as boolean,
            isDeleteInProgress: false as boolean,
            viewingTaskDetails: false as boolean,
            reassignInProgress: false as boolean,
            isSaving: false as boolean,
            loadingUsers: false as boolean,
            employeeList: [] as IEmployeeList[],
            scheduleEmployee: {} as ITypeaheadOptions,
            taskStatusNameColor,
            formatDate
        };
    },
    created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._assetService = Container.instance.get(AssetsService);
        this._downloadService = Container.instance.get(DownloadService);
        this._printService = Container.instance.get(PrintService);
        this._navigationService = Container.instance.get(NavigationService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._analyticsService = Container.instance.get(AnalyticsService);
    },
    computed: {
        cantDeleteOrReassignTask() {
            const completedTaskStatuses = [
                "Submitted Pending Co-Signature",
                "Submitted with Signature",
                "Submitted With Signature",
                "Export Ready",
                "To Be Sent",
                "Sent to Physician",
                "Returned with Physician Signature",
                "Completed",
                "Not Exported"
            ];
            return completedTaskStatuses.includes(this.task.Status);
        }
    },
    methods: {
        async handleTaskClick(task: ITaskPatientChart) {
            let redirectResponse;
            let analyticsCategory = "Calendar-Task-Navigation";
            let action = "";
            redirectResponse = await this._navigationService.navigateToTask({
                agencyId: this.task.AgencyId,
                applicationId: this.task.Application,
                userId: task.UserId,
                isClinician: true,
                patientId: this.task.PatientId,
                taskId: task.Id,
                episodeId: task.EpisodeId
            });
            action = "AxxessHomeCare";
            this._navigationService.redirect(redirectResponse, {
                category: analyticsCategory,
                action
            });
        },
        async handleEmployeeNameClick(task: ITaskPatientChart) {
            const isClinician = this._permissionManager.checkPermission(
                ParentPermissionEnum.clinician,
                PermissionActionEnum.canView
            );
            const redirectResponse = await this._navigationService.navigateToUser({
                agencyId: this.task.AgencyId,
                applicationId: this.task.Application,
                isClinician: isClinician,
                userId: this.task.UserId
            });
            const action = "AxxessHomeCare";
            this._navigationService.redirect(redirectResponse, {
                category: AnalyticsCategory.CentralCalendarNavigation,
                action
            });
        },
        getEmployeeName(task: ITaskPatientChart) {
            return task.UserFirstName ? `${task.UserFirstName} ${task.UserLastName}` : "";
        },
        async downloadVisit() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Download"
            });
            try {
                this.downloadInProgress = true;
                const payload = {
                    agencyId: this.task.AgencyId,
                    patientId: this.task.PatientId,
                    application: this.task.Application
                };
                const response = await this._assetService.downloadPatientVisit(this.task.Id, payload);
                this._downloadService.downloadFile(response);
                this._toastrService.success({
                    title: `Task Details Downloaded`,
                    message: `Task has been successfully downloaded.`
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Failed To Download Task `,
                    message: `Failed To Download Task. Try Again !!`
                });
            } finally {
                this.downloadInProgress = false;
            }
        },
        async printVisit() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Print"
            });
            try {
                this.printingInProgress = true;
                const payload = {
                    agencyId: this.task.AgencyId,
                    patientId: this.task.PatientId,
                    application: this.task.Application
                };
                const response = await this._assetService.downloadPatientVisit(this.task.Id, payload);
                this._printService.print(response);
                this._toastrService.success({
                    title: `Task Details Printed Successfully !!`,
                    message: `Task has been successfully printed.`
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Failed to print task details !!`,
                    message: `Failed to print. Try Again !!`
                });
            } finally {
                this.printingInProgress = false;
            }
        },
        requestDeleteTask() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Delete"
            });
            this.isDeleteRequested = true;
        },
        async deleteTask() {
            try {
                this.isDeleteInProgress = true;
                const payload = {
                    AgencyId: this.task.AgencyId,
                    Application: this.task.Application,
                    PatientId: this.task.PatientId,
                    EventId: [this.task.Id],
                    IsPatient: true
                };
                const response = await this._taskService.patientBulkDelete(payload);
                if (response.isSuccessful) {
                    this._toastrService.success({
                        title: `Task Deleted`,
                        message: `Task has been successfully deleted.`
                    });
                }
                this.getPatientTasks();
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Failed Task Deletion`,
                    message: `Failed to delete Task`
                });
            } finally {
                this.isDeleteInProgress = false;
                this.isDeleteRequested = false;
            }
        },
        handleViewTaskDetails() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Task Details"
            });
            this.viewingTaskDetails = true;
        },
        cancelViewTaskDetails() {
            this.viewingTaskDetails = false;
            this.isSaving = false;
        },
        savedTaskDetails() {
            this.viewingTaskDetails = false;
            this.isSaving = false;
            this.getPatientTasks();
        },
        async reassignVisit() {
            try {
                this.isSaving = true;
                const payload = {
                    AccountId: this.task.AgencyId,
                    UserId: this.scheduleEmployee.value,
                    Application: this.task.Application,
                    EpisodeId: this.task.EpisodeId,
                    PatientId: this.task.PatientId,
                    TaskId: this.task.Id
                };
                const response = await this._taskService.reassignVisit(payload);
                if (response && !response?.isSuccessful) {
                    if (response.errorDetails.taskIssues.length) {
                        response.errorDetails.taskIssues.forEach((taskIssue: any) => {
                            this._toastrService.error({
                                title: `Visit Reassigning Failed`,
                                message: taskIssue.message
                            });
                        });
                    } else {
                        this._toastrService.error({
                            title: `Visit Reassigning Failed`,
                            message: `Reassigning Visit Operation Failed, Please Contact Axxess if issue still persists.`
                        });
                    }
                } else {
                    this._toastrService.success({
                        title: `Visit Reassigned`,
                        message: `Visit has been successfully reassigned.`
                    });
                    this.getPatientTasks();
                }
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Visit Reassigning Failed`,
                    message: `Reassigning Visit Operation Failed, Please Contact Axxess if issue still persists.`
                });
            } finally {
                this.isSaving = false;
                this.reassignInProgress = false;
            }
        },
        async handleReassignVisit() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Reassign"
            });
            this.reassignInProgress = true;
            this.loadingUsers = true;
            const queryParams = {
                PatientId: this.task.PatientId,
                AgencyId: this.task.AgencyId,
                LocationId: this.task.LocationId,
                Application: this.task.Application
            };
            const response = (await this._taskService.homecareEmployeePreference(queryParams)) as any;
            this.employeeList = response;
            this.loadingUsers = false;
        },
        employeeListFilter(filter: string) {
            if (filter) {
                const filterEmployeeList = this.employeeList.filter((employee: any) => {
                    const fullName = `${employee.FirstName} ${employee.LastName}`;
                    return fullName.toLowerCase().includes(filter.toLowerCase());
                });
                return filterEmployeeList?.map((item: any) => {
                    return {
                        name: `${item.FirstName} ${item.LastName}`,
                        value: item.Id
                    };
                });
            } else {
                return this.employeeList?.map((item: any) => ({
                    value: item.Id,
                    name: `${item.FirstName} ${item.LastName}`
                }));
            }
        },
        changescheduleEmployee(data: any) {
            this.scheduleEmployee = data;
        },
        handleCancelReassign() {
            this.reassignInProgress = false;
            this.scheduleEmployee = {} as ITypeaheadOptions;
        },
        cancelDeleteTask() {
            this.isDeleteRequested = false;
        }
    }
});
